import { Injectable, Injector, OnDestroy } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { NavigationEnd, Router } from "@angular/router";
import { combineLatest, Subscription, timer } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { AuthService } from "@auth0/auth0-angular";
import { AnalyticsClientContextService } from "./analytics-client-context.service";

export interface AnalyticsTrackOptions {
  screen?: string;
  entityType?: string;
  entityId?: string;
  properties?: {[key: string]: any};
}

interface AnalyticsEventPayload extends AnalyticsTrackOptions {
  eventType: string;
  platform: string;
  sessionId: string;
  anonymousId: string;
  deviceId: string;
  browser: string;
  os: string;
  deviceType: string;
  webCommit?: string;
  clientVersion?: string;
}

@Injectable({ providedIn: "root" })
export class AnalyticsService implements OnDestroy {
  private static readonly SESSION_STARTED_KEY = "nogosh.analytics.sessionStarted";
  private static readonly LOGIN_SUCCESS_KEY = "nogosh.analytics.loginSuccess";
  private static readonly MAX_QUEUE_SIZE = 200;
  private static readonly FLUSH_INTERVAL_MS = 15000;

  private readonly apiUrl = environment.API_BASE_URL + "/analytics/events";
  private queue: AnalyticsEventPayload[] = [];
  private flushing = false;
  private routeSubscription: Subscription;
  private flushSubscription: Subscription;
  private beforeUnloadHandler = () => this.flush();

  constructor(
    private http: HttpClient,
    private clientContext: AnalyticsClientContextService,
    private injector: Injector
  ) {
    this.flushSubscription = timer(AnalyticsService.FLUSH_INTERVAL_MS, AnalyticsService.FLUSH_INTERVAL_MS)
      .subscribe(() => this.flush());
    window.addEventListener("beforeunload", this.beforeUnloadHandler);
  }

  public initializeRouteTracking(router: Router): void {
    if (this.routeSubscription) {
      return;
    }

    const auth = this.injector.get(AuthService);
    this.routeSubscription = combineLatest([
      router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)),
      auth.isLoading$,
      auth.isAuthenticated$
    ]).pipe(
      filter(([, loading]) => !loading),
      map(([event, , authenticated]) => ({
        path: this.analyticsPath(event.urlAfterRedirects),
        screen: this.screenNameForUrl(event.urlAfterRedirects, authenticated)
      })),
      distinctUntilChanged((previous, current) => previous.path === current.path && previous.screen === current.screen)
    ).subscribe(({path, screen}) => {
      this.track("screen_viewed", { screen, properties: { path } });
      this.flush();
    });
  }

  public trackSessionStarted(): void {
    if (sessionStorage.getItem(AnalyticsService.SESSION_STARTED_KEY)) {
      return;
    }

    sessionStorage.setItem(AnalyticsService.SESSION_STARTED_KEY, "true");
    this.track("session_started", { screen: this.screenNameForUrl(window.location.pathname) });
  }

  public trackLoginSuccess(): void {
    if (sessionStorage.getItem(AnalyticsService.LOGIN_SUCCESS_KEY)) {
      return;
    }

    sessionStorage.setItem(AnalyticsService.LOGIN_SUCCESS_KEY, "true");
    this.track("login_success", { screen: this.screenNameForUrl(window.location.pathname) });
  }

  public track(eventType: string, options: AnalyticsTrackOptions = {}): void {
    if (!eventType) {
      return;
    }

    const context = this.clientContext.getContext();
    const event: AnalyticsEventPayload = {
      eventType,
      platform: context.platform,
      sessionId: context.sessionId,
      anonymousId: context.anonymousId,
      deviceId: context.deviceId,
      browser: context.browser,
      os: context.os,
      deviceType: context.deviceType,
      clientVersion: context.clientVersion,
      webCommit: context.webCommit,
      screen: options.screen,
      entityType: options.entityType,
      entityId: options.entityId,
      properties: this.sanitizeProperties(options.properties || {})
    };

    this.queue.push(event);
    if (this.queue.length > AnalyticsService.MAX_QUEUE_SIZE) {
      this.queue.splice(0, this.queue.length - AnalyticsService.MAX_QUEUE_SIZE);
    }
  }

  public flush(): void {
    if (this.flushing || this.queue.length === 0) {
      return;
    }

    const events = this.queue.splice(0, this.queue.length);
    this.flushing = true;
    this.http.post(this.apiUrl, { events }).subscribe(
      () => this.flushing = false,
      () => {
        this.queue = events.concat(this.queue).slice(-AnalyticsService.MAX_QUEUE_SIZE);
        this.flushing = false;
      }
    );
  }

  public trackAndFlush(eventType: string, options: AnalyticsTrackOptions = {}): void {
    this.track(eventType, options);
    this.flush();
  }

  public ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.flushSubscription) {
      this.flushSubscription.unsubscribe();
    }
    window.removeEventListener("beforeunload", this.beforeUnloadHandler);
  }

  private sanitizeProperties(properties: {[key: string]: any}): {[key: string]: any} {
    const sanitized: {[key: string]: any} = {};
    Object.keys(properties || {}).forEach((key) => {
      if (this.isPrivateKey(key)) {
        return;
      }

      const value = properties[key];
      if (value === null || value === undefined) {
        return;
      }

      if (typeof value === "string") {
        sanitized[key] = value.length > 120 ? value.substring(0, 120) : value;
      } else if (typeof value === "number" || typeof value === "boolean") {
        sanitized[key] = value;
      }
    });
    return sanitized;
  }

  private isPrivateKey(key: string): boolean {
    const normalized = key.toLowerCase();
    return normalized.indexOf("token") >= 0 ||
      normalized.indexOf("password") >= 0 ||
      normalized.indexOf("authorization") >= 0 ||
      normalized.indexOf("email") >= 0 ||
      normalized.indexOf("phone") >= 0 ||
      normalized.indexOf("message") >= 0 ||
      normalized.indexOf("text") >= 0 ||
      normalized.indexOf("useragent") >= 0;
  }

  private screenNameForUrl(url: string, authenticated = true): string {
    const cleanUrl = this.safePath(url);
    if (cleanUrl === "/" || cleanUrl.indexOf("/event-list") === 0) return authenticated ? "matches_list" : "public_landing";
    if (cleanUrl.indexOf("/event-detail") === 0) return "match_detail";
    if (cleanUrl.indexOf("/event-players") === 0) return "match_players";
    if (cleanUrl.indexOf("/event-player-management") === 0) return "match_player_management";
    if (cleanUrl.indexOf("/messages") === 0) return "chat";
    if (cleanUrl.indexOf("/chat-list") === 0) return "chat_list";
    if (cleanUrl.indexOf("/chat-detail") === 0) return "chat_detail";
    if (cleanUrl.indexOf("/player-detail") === 0) return "profile";
    if (cleanUrl.indexOf("/claim/") === 0) return "guest_claim";
    if (cleanUrl.indexOf("/playspace") === 0) return "playspace";
    if (cleanUrl.indexOf("/help") === 0) return "help";
    if (cleanUrl.indexOf("/feedback") === 0) return "feedback";
    return "unknown";
  }

  private safePath(url: string): string {
    const path = (url || "").split("?")[0].split("#")[0];
    return path || "/";
  }

  private analyticsPath(url: string): string {
    return this.safePath(url)
      .replace(/\/claim\/[^/]+/g, "/claim/:token")
      .replace(/\/join\/[^/]+/g, "/join/:inviteToken")
      .replace(/\/public-event-invite\/[^/]+/g, "/public-event-invite/:inviteToken")
      .replace(/\/i\/[^/]+/g, "/i/:inviteToken")
      .replace(/[0-9a-f]{24}/gi, ":id")
      .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":id");
  }
}
